import { useState, useMemo } from 'react'
import { Toggle } from '../components/Toggle'
import { PencilIcon } from '../components/icons/PencilIcon'
import { MCPIcon } from '../components/icons/MCPIcon'
import { LinkIcon } from '../components/icons/LinkIcon'
import { InfoIcon } from '../components/icons/InfoIcon'
import { InfoTooltip } from '../components/InfoTooltip'
import { WarningIcon } from '../components/icons/WarningIcon'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useApiFetch } from '../lib/api'
import { Card } from '../components/Card'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OperationsTree } from '../components/OperationsTree'
import type { OperationsTreeOp } from '../components/OperationsTree'
import type {
  Connection,
  ConnectionOperation,
  ConnectionListResponse,
  ConnectionBindingRef,
} from '../lib/types'
import { Spinner } from '../components/Spinner'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'
import { FEATURES } from '../lib/features'

/* ─── Props ─── */

export interface ConnectionsTabProps {
  agentId: string
  connectionBindings: ConnectionBindingRef[]
  onConnectionToggle: (connectionId: string, isEnabled: boolean) => void
  onOperationToggle: (connectionId: string, operationId: string, isEnabled: boolean) => void
  isReadOnly?: boolean
}

/* ─── Main ConnectionsTab component ─── */

export function ConnectionsTab({
  connectionBindings,
  onConnectionToggle,
  onOperationToggle,
  isReadOnly = false,
}: Readonly<ConnectionsTabProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null)
  const [disableConnConfirm, setDisableConnConfirm] = useState<Connection | null>(null)

  const connectionsQuery = useInfiniteQuery({
    queryKey: ['connections-list'],
    queryFn: ({ pageParam }) => {
      const url = pageParam
        ? `/api/v1/connections?cursor=${pageParam}`
        : '/api/v1/connections'
      return apiFetch<ConnectionListResponse>(url)
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? null,
  })

  const connections: Connection[] = connectionsQuery.data?.pages.flatMap(p => p.items) ?? []
  const sortedConnections = [...connections].sort((a, b) => {
    if (a.isEssential !== b.isEssential) return a.isEssential ? -1 : 1
    return a.name.localeCompare(b.name)
  })
  const selectedConnection = connections.find(c => c.id === selectedConnectionId) ?? null

  // Entity-editor registry (Connection/Agent) — unlike Skill/Knowledge, there is
  // no separate free-text draft here: every toggle commits straight into the
  // page-level connections form (already covered by the page's own dirty
  // tracking), so this registration is always clean by construction. Registered
  // for completeness/parity with the other five editor kinds.
  useEntityEditorRegistration({
    isOpen: () => selectedConnectionId !== null,
    isDirty: () => false,
    markClean: () => setSelectedConnectionId(null),
  })

  function connectionIsEnabled(connectionId: string): boolean {
    return connectionBindings.find(b => b.connectionId === connectionId)?.isEnabled ?? false
  }

  function operationIsEnabled(connectionId: string, operationId: string): boolean {
    const conn = connectionBindings.find(b => b.connectionId === connectionId)
    return conn?.operations.find(o => o.operationId === operationId)?.isEnabled ?? false
  }

  function connectionGloballyEnabled(connectionId: string): boolean {
    return connections.some(conn => conn.id === connectionId && conn.isEnabled)
  }

  function operationGloballyEnabled(connectionId: string, operationId: string): boolean {
    const conn = connections.find(c => c.id === connectionId)
    return conn ? conn.operations.some(op => op.id === operationId && op.isEnabled) : true
  }

  return (
    <div>
      <Card
        title={t('connectionsTab.title')}
        subtitle={t('connectionsTab.subtitle')}
        actions={
          FEATURES.globalConnectionsConfig ? (
            <button
              type="button"
              onClick={() => navigate('/global-settings/connections')}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '6px 12px', borderRadius: 6,
                fontSize: 12, fontWeight: 600,
                color: 'var(--text-secondary)', background: 'transparent',
                border: '1px solid var(--border)', textDecoration: 'none',
                whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer',
              }}
            >
              {t('common.manageInGlobalSettings')}
            </button>
          ) : undefined
        }
        noPadding
        className="editor-split-card"
      >
        <div className="editor-split-body">

          {/* ── Left: connection list ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {connectionsQuery.isLoading && (
              <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                <Spinner size={20} />
              </div>
            )}
            {connectionsQuery.isError && (
              <div style={{ padding: '12px 20px', color: 'var(--danger)', fontSize: 13 }}>
                {t('connectionsTab.failedToLoad')}
              </div>
            )}
            {!connectionsQuery.isLoading && !connectionsQuery.isError && sortedConnections.length === 0 && (
              <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                {t('connectionsTab.noConnections')}
              </div>
            )}

            {sortedConnections.map((conn) => {
              const isEnabled = connectionIsEnabled(conn.id)
              const isGloballyEnabled = connectionGloballyEnabled(conn.id)
              const isSelected = selectedConnectionId === conn.id

              return (
                <div
                  key={conn.id}
                  className={`skill-row${isSelected ? ' selected' : ''}`}
                  onClick={() => setSelectedConnectionId(conn.id)}
                  title={!isGloballyEnabled ? t('connectionsTab.globallyDisabled') : undefined}
                >
                  <div className="conn-icon icon-mcp"><MCPIcon /></div>

                  <div className="skill-info">
                    <div className="skill-name">
                      {conn.name}
                    </div>
                    {conn.description && (
                      <div className="skill-desc">{conn.description}</div>
                    )}
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <Toggle
                      checked={isEnabled}
                      onChange={(v) => {
                        if (!v) setDisableConnConfirm(conn)
                        else onConnectionToggle(conn.id, v)
                      }}
                      label={t('connectionsTab.enableForSpark', { name: conn.name })}
                      disabled={!isGloballyEnabled}
                    />
                  </div>

                  <button
                    type="button"
                    className="skill-edit-btn spark-conn-edit-btn"
                    onClick={(e) => { e.stopPropagation(); setSelectedConnectionId(conn.id) }}
                    aria-label={t('connectionsTab.manageOpsAria', { name: conn.name })}
                    title={t('connectionsTab.manageOps')}
                  >
                    <PencilIcon />
                  </button>
                </div>
              )
            })}

            {connectionsQuery.hasNextPage && (
              <div style={{ padding: '8px 16px' }}>
                <button
                  type="button"
                  onClick={() => connectionsQuery.fetchNextPage()}
                  disabled={connectionsQuery.isFetchingNextPage}
                  style={{
                    width: '100%', padding: '6px 0', borderRadius: 6,
                    fontSize: 12, fontWeight: 500,
                    color: 'var(--accent)', background: 'transparent',
                    border: '1px solid var(--border)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: connectionsQuery.isFetchingNextPage ? 0.6 : 1,
                  }}
                >
                  {connectionsQuery.isFetchingNextPage
                    ? <><Spinner size={12} /> {t('common.loading')}</>
                    : t('agents.loadMore')}
                </button>
              </div>
            )}
          </div>

          {/* separator */}
          <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

          {/* ── Right: operations pane ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, padding: '20px 24px', overflowY: 'auto' }}>
            {selectedConnection === null ? (
              <div
                id="conn-editor-empty"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12,
                  color: 'var(--text-tertiary)',
                }}
              >
                <LinkIcon />
                <p style={{ fontSize: 13, margin: 0 }}>
                  {t('connectionsTab.clickToManage')}
                </p>
              </div>
            ) : (
              <OperationsPane
                connection={selectedConnection}
                connectionBinding={connectionBindings.find(b => b.connectionId === selectedConnection.id)}
                operationIsEnabled={operationIsEnabled}
                operationGloballyEnabled={operationGloballyEnabled}
                onOperationToggle={onOperationToggle}
                parentEnabled={connectionIsEnabled(selectedConnection.id)}
                readOnlyAgent={isReadOnly}
              />
            )}
          </div>
        </div>
      </Card>

      {disableConnConfirm && (
        <ConfirmDialog
          title={t('connectionsTab.disableDialog.title', { name: disableConnConfirm.name })}
          message={t('connectionsTab.disableDialog.message')}
          body={disableConnConfirm.isEssential
            ? <p style={{ fontSize: 12, color: 'var(--warning)', marginTop: 6 }}>
                {t('connectionsTab.disableDialog.essentialWarning')}
              </p>
            : undefined}
          confirmLabel={t('connectionsTab.disableDialog.confirm')}
          danger
          onConfirm={() => { onConnectionToggle(disableConnConfirm.id, false); setDisableConnConfirm(null) }}
          onCancel={() => setDisableConnConfirm(null)}
        />
      )}
    </div>
  )
}

/* ─── Operations pane ─── */

/** A connection operation with a materialized id — always true for API-fetched data
 * (the backend's ConnectionOperation schema requires `id`); the frontend type is
 * defensively nullable for shared use in draft/discovery flows elsewhere. */
type KnownOperation = ConnectionOperation & { id: string }

interface OperationsPaneProps {
  connection: Connection
  connectionBinding: ConnectionBindingRef | undefined
  operationIsEnabled: (connectionId: string, operationId: string) => boolean
  operationGloballyEnabled: (connectionId: string, operationId: string) => boolean
  onOperationToggle: (connectionId: string, operationId: string, isEnabled: boolean) => void
  parentEnabled: boolean
  /** Ask Mambu and other Mambu-built agents are read-only: Execute operations are never offered. */
  readOnlyAgent: boolean
}

function OperationsPane({
  connection,
  connectionBinding,
  operationIsEnabled,
  operationGloballyEnabled,
  onOperationToggle,
  parentEnabled,
  readOnlyAgent,
}: OperationsPaneProps) {
  const { t } = useTranslation()
  const [disableDeprecatedConfirm, setDisableDeprecatedConfirm] = useState<KnownOperation | null>(null)

  const knownOps = useMemo(
    () => connection.operations.filter((op): op is KnownOperation =>
      op.id != null && (!readOnlyAgent || op.type === 'Read')
    ),
    [connection.operations, readOnlyAgent],
  )
  const opsById = useMemo(() => new Map(knownOps.map(op => [op.id, op])), [knownOps])

  const toTreeOp = (op: KnownOperation): OperationsTreeOp => ({
    operationId: op.id, key: op.key, name: op.name, category: op.category, type: op.type,
  })
  const treeOps = useMemo(
    () => knownOps.filter(op => !op.isDeprecated).map(toTreeOp),
    [knownOps],
  )
  const deprecatedTreeOps = useMemo(
    () => knownOps.filter(op => op.isDeprecated).map(toTreeOp),
    [knownOps],
  )

  function isChecked(operationId: string): boolean {
    return operationIsEnabled(connection.id, operationId)
  }

  function handleToggle(operationId: string, checked: boolean) {
    const op = opsById.get(operationId)
    if (op?.isDeprecated) {
      // Deprecated ops can only ever be turned off, and only after a confirmation —
      // the toggle is disabled (via isRowDisabled) whenever one is already off.
      if (isChecked(operationId)) setDisableDeprecatedConfirm(op)
      return
    }
    onOperationToggle(connection.id, operationId, checked)
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t('connectionsTab.ops.title')}
            <InfoTooltip icon={<InfoIcon size={13} />} width={260}>
              {t('connectionsTab.ops.infoTooltip')}
            </InfoTooltip>
          </div>
          <span className="badge badge-read" style={{ fontSize: 11 }}>{connection.name}</span>
        </div>

        <OperationsTree
          ops={treeOps}
          mode="agent"
          isChecked={isChecked}
          onToggle={handleToggle}
          isTenantEnabled={(operationId) => operationGloballyEnabled(connection.id, operationId)}
          disabled={!parentEnabled}
          ariaLabel={t('connectionsTab.ops.treeAriaLabel', { name: connection.name })}
          toggleAriaLabel={(op) => t('connectionsTab.enableForSpark', { name: op.name })}
          categoryToggleAriaLabel={(category) => t('connectionsTab.toggleAllCategory', { category })}
          isRowDisabled={(op) => {
            const raw = opsById.get(op.operationId)
            return raw?.isDeprecated ? !isChecked(op.operationId) : false
          }}
          renderRowExtra={(op) => {
            const raw = opsById.get(op.operationId)
            const isNew = !raw?.isDeprecated && !connectionBinding?.operations.some(o => o.operationId === op.operationId)
            const isBlocked = !raw?.isDeprecated && !operationGloballyEnabled(connection.id, op.operationId)
            return (
              <>
                {/* The deprecated warning triangle is rendered by the tree itself
                    (prototype :7546-7548) — duplicating it here would show two. */}
                {isNew && <span className="badge badge-new">New</span>}
                {isBlocked && (
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    {t('connectionsTab.ops.globallyDisabledHint')}
                  </span>
                )}
              </>
            )
          }}
          deprecatedOps={deprecatedTreeOps}
          deprecatedGroupLabel={<><WarningIcon />{t('connectionsTab.ops.deprecatedGroup')}</>}
          hideExecuteFilter={readOnlyAgent}
          copy={{
            filtering: t('connectionsTab.ops.filtering'),
            typeFilter: t('connectionsTab.ops.typeFilter'),
            statusFilter: t('connectionsTab.ops.statusFilter'),
            quickActions: t('connectionsTab.ops.quickActions'),
            readOnlyPreset: t('connectionsTab.ops.readOnlyPreset'),
            enableShown: t('connectionsTab.ops.enableShown'),
            disableShown: t('connectionsTab.ops.disableShown'),
            overview: t('connectionsTab.ops.overview'),
            countEnabled: (enabled, total) => (
              <Trans
                i18nKey="connectionsTab.ops.countEnabled"
                values={{ enabled, total }}
                components={[<strong key="enabled" />]}
              />
            ),
            noMatch: t('connectionsTab.ops.noMatch'),
            lockedTooltip: t('connectionsTab.ops.lockedTooltip'),
            deprecatedTooltip: t('connectionsTab.deprecationWarning'),
          }}
        />

        {!parentEnabled && (
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: -6 }}>
            {t('connectionsTab.ops.enableConnectionFirst')}
          </div>
        )}
      </div>

      {disableDeprecatedConfirm && (
        <ConfirmDialog
          title={t('connectionsTab.deprecateDisableDialog.title', { name: disableDeprecatedConfirm.name })}
          message={t('connectionsTab.deprecateDisableDialog.message')}
          confirmLabel={t('connectionsTab.deprecateDisableDialog.confirm')}
          danger
          onConfirm={() => {
            onOperationToggle(connection.id, disableDeprecatedConfirm.id, false)
            setDisableDeprecatedConfirm(null)
          }}
          onCancel={() => setDisableDeprecatedConfirm(null)}
        />
      )}
    </>
  )
}
